'use client';

import React, { Children, cloneElement, ReactElement, useEffect, useId, useState } from 'react';
import { VisuallyHidden } from '../../../helpers';
import clsx from 'classnames';

import styles from './Radio.module.scss';
import { RadioGroupProps, RadioProps } from './types';

export function RadioGroup({
	children,
	containerClassName,
	value: initialValue,
	...props
}: RadioGroupProps) {
	const [value, setValue] = useState<string>(initialValue ?? '');
	const options = Children.toArray(children);
	const id = useId();

	useEffect(() => {
		if (!initialValue) return;
		setValue(initialValue);
	}, [initialValue]);

	return (
		<div className={containerClassName}>
			{props.label ? <span>{props.label}</span> : null}
			{options.map((option, index) => {
				return cloneElement(
					option as ReactElement<RadioProps>,
					{
						name: `radio-${id}`,
						checked: (option as ReactElement<RadioProps>).props.value === value,
						onChange: (event) => {
							setValue(event.target.value);
							props?.onChange?.(event.target.value);
						},
					} as RadioProps
				);
			})}
		</div>
	);
}

export function Radio({
	children,
	disabled = false,
	labelClassName,
	position = 'start',
	spaceBetween = position === 'end',
	checked,
	name,
	value,
	onChange,
}: RadioProps) {
	const labelClasses = clsx(
		styles.radioLabel,
		{
			[styles['radioLabel--reversed']]: position === 'end',
			[styles['radioLabel--with-spacing']]: spaceBetween,
		},
		labelClassName
	);

	const inputClasses = clsx(styles.radioInput, {
		[styles['radioInput--checked']]: checked,
	});

	return (
		<label className={labelClasses}>
			<VisuallyHidden>
				<input
					type="radio"
					name={name}
					disabled={disabled}
					value={value}
					checked={checked}
					onChange={onChange}
				/>
			</VisuallyHidden>
			<div className={inputClasses} />
			<span className={styles.radioText}>{children}</span>
		</label>
	);
}
